import {ReactNode, useState} from 'react';
import {useQueryClient} from 'react-query';
import {useIntl} from 'react-intl';
import {
  Button as BootstrapButton,
  Card,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import ActionIcon from '../../../../../Components/ActionIcon';
import Badge from '../../../../../app/common/components/Badge';
import Button from '../../../../../Components/Button';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import {ActionIvr, Ivr, SVI_TOUCHES, IVR_MAX, TypeActionIvr} from '../TrunkSip/mocks';
import {
  IVRS_QUERY_KEY,
  useGetIvrs,
  useGetPbxGroupes,
  useGetSons,
  useGetVirtualPbxActivite,
  useGetVirtualPbxNumeros,
} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import {NUMEROS_COURTS} from './Groupes';
import Loading from '../../../../../app/common/components/Loading';

/**
 * MAQUETTE — reproduction fidèle de « IVRList.aspx » (liste) + « IVRUpdate.aspx »
 * (édition détaillée), à partir de l'analyse du front-end legacy. Contrairement au
 * legacy (postbacks séquentiels : groupe, puis annonce, puis chaque action), la
 * sauvegarde est atomique ici — un seul IVR (nom + numéro court + annonce + actions)
 * enregistré en une fois, comme le recommande l'analyse.
 */

const ACTION_LABEL_ID: Record<TypeActionIvr, string> = {
  numero: 'TELEPHONIE.SVI.IVR.ACTION.NUMERO',
  groupe: 'TELEPHONIE.SVI.IVR.ACTION.GROUPE',
  annonce: 'TELEPHONIE.SVI.IVR.ACTION.ANNONCE',
  fin_appel: 'TELEPHONIE.SVI.IVR.ACTION.FIN_APPEL',
  rejouer_menu: 'TELEPHONIE.SVI.IVR.ACTION.REJOUER_MENU',
};

const TYPES_ACTION: TypeActionIvr[] = ['numero', 'groupe', 'annonce', 'fin_appel', 'rejouer_menu'];

const NUMERO_REGEX = /^\+41[0-9]{9}$|^\+[0-9]{3,29}$/;
const GROUPE_COURT_REGEX = /^[0-9]{3}$/;

const jouerApercu = (): void => {
  try {
    const AudioContextClasse =
      window.AudioContext || (window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext;
    const contexte = new AudioContextClasse();
    const oscillateur = contexte.createOscillator();
    const gain = contexte.createGain();
    oscillateur.type = 'sine';
    oscillateur.frequency.value = 440;
    oscillateur.connect(gain);
    gain.connect(contexte.destination);
    gain.gain.setValueAtTime(0.1, contexte.currentTime);
    oscillateur.start();
    oscillateur.stop(contexte.currentTime + 0.3);
  } catch {
    // Web Audio API indisponible — pas d'aperçu sonore possible.
  }
};

const DestinataireTexte = ({action}: {action: ActionIvr}): JSX.Element => {
  if (action.type === 'fin_appel' || action.type === 'rejouer_menu') {
    return <span className="text-muted">—</span>;
  }
  return <span>{action.cible}</span>;
};

interface FormulaireIvrProps {
  ivrInitial?: Ivr;
  onAnnuler: () => void;
  onEnregistrer: (ivr: Omit<Ivr, 'id'>) => void;
}

const FormulaireIvr = ({ivrInitial, onAnnuler, onEnregistrer}: FormulaireIvrProps): JSX.Element | null => {
  const intl = useIntl();
  const {data: groupesData, isLoading: isLoadingGroupes} = useGetPbxGroupes();
  const {data: annoncesData, isLoading: isLoadingAnnonces} = useGetSons('annonce_fichier');
  const {data: activiteData, isLoading: isLoadingActivite} = useGetVirtualPbxActivite();
  const modeEdition = ivrInitial != null;

  const [nom, setNom] = useState(ivrInitial?.nom ?? '');
  const [numeroCourt, setNumeroCourt] = useState(ivrInitial?.numeroCourt ?? NUMEROS_COURTS[0]);
  const [annonceAccueil, setAnnonceAccueil] = useState(ivrInitial?.annonceAccueil ?? '');
  const [actions, setActions] = useState<ActionIvr[]>(ivrInitial?.actions ?? []);
  const [erreur, setErreur] = useState<string | null>(null);

  const [toucheEnEdition, setToucheEnEdition] = useState<string | null>(null);
  const [toucheChoisie, setToucheChoisie] = useState('');
  const [typeChoisi, setTypeChoisi] = useState<TypeActionIvr>('groupe');
  const [cibleChoisie, setCibleChoisie] = useState('');
  const [saisieLibreGroupe, setSaisieLibreGroupe] = useState(false);
  const [saisieLibreNumero, setSaisieLibreNumero] = useState(false);

  if (isLoadingGroupes || !groupesData || isLoadingAnnonces || !annoncesData || isLoadingActivite || !activiteData) {
    return <Loading className="col-md-6" />;
  }

  const touchesDisponibles = SVI_TOUCHES.filter(
    (touche) => touche === toucheEnEdition || !actions.some((action) => action.touche === touche)
  );

  const commencerAjoutAction = (): void => {
    setToucheEnEdition(null);
    setToucheChoisie(touchesDisponibles[0] ?? '');
    setTypeChoisi('groupe');
    setCibleChoisie('');
    setSaisieLibreGroupe(false);
    setSaisieLibreNumero(false);
  };

  const commencerEditionAction = (action: ActionIvr): void => {
    setToucheEnEdition(action.touche);
    setToucheChoisie(action.touche);
    setTypeChoisi(action.type);
    setCibleChoisie(action.cible ?? '');
    setSaisieLibreGroupe(false);
    setSaisieLibreNumero(false);
  };

  const actionValide =
    toucheChoisie !== '' &&
    (typeChoisi === 'fin_appel' ||
      typeChoisi === 'rejouer_menu' ||
      (typeChoisi === 'groupe' && saisieLibreGroupe
        ? GROUPE_COURT_REGEX.test(cibleChoisie)
        : typeChoisi === 'numero' && saisieLibreNumero
          ? NUMERO_REGEX.test(cibleChoisie)
          : cibleChoisie !== ''));

  const validerAction = (): void => {
    if (!actionValide) {
      return;
    }
    const nouvelleAction: ActionIvr = {
      touche: toucheChoisie,
      type: typeChoisi,
      cible: typeChoisi === 'fin_appel' || typeChoisi === 'rejouer_menu' ? undefined : cibleChoisie,
    };
    setActions(
      toucheEnEdition
        ? actions.map((action) => (action.touche === toucheEnEdition ? nouvelleAction : action))
        : [...actions, nouvelleAction]
    );
    commencerAjoutAction();
  };

  const supprimerAction = (touche: string): void => {
    setActions(actions.filter((action) => action.touche !== touche));
  };

  const enregistrer = (): void => {
    if (nom.trim() === '') {
      return;
    }
    if (annonceAccueil.trim() === '') {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.ERREUR_ANNONCE'}));
      return;
    }
    setErreur(null);
    onEnregistrer({nom: nom.trim(), numeroCourt, annonceAccueil, actions});
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h3 className="fw-bold text-uppercase m-0">
          {intl.formatMessage({
            id: modeEdition ? 'TELEPHONIE.SVI.IVR.FORM.TITLE_EDIT' : 'TELEPHONIE.SVI.IVR.FORM.TITLE_ADD',
          })}
        </h3>
        <BootstrapButton variant="outline-secondary" size="sm" onClick={onAnnuler}>
          <i className="bi bi-arrow-left me-2" />
          {intl.formatMessage({id: 'COMMON.BACK'})}
        </BootstrapButton>
      </div>

      {erreur && <div className="fw-bold text-danger mb-3">{erreur}</div>}

      <Card bg="light" className="mb-5">
        <Card.Body>
          <Row className="g-4">
            <Col xs={12} md={6}>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.NOM'})}
              </Form.Label>
              <Form.Control value={nom} onChange={(e) => setNom(e.target.value)} />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.NUMERO_COURT'})}
              </Form.Label>
              <Form.Select
                style={{maxWidth: 200}}
                value={numeroCourt}
                onChange={(e) => setNumeroCourt(e.target.value)}
              >
                {NUMEROS_COURTS.map((numero) => (
                  <option key={numero} value={numero}>
                    {numero}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.ANNONCE_ACCUEIL'})}
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                <Form.Select
                  style={{maxWidth: 260}}
                  value={annonceAccueil}
                  onChange={(e) => setAnnonceAccueil(e.target.value)}
                >
                  <option value="">
                    {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.CHOISIR_ANNONCE'})}
                  </option>
                  {annoncesData.map((son) => (
                    <option key={son.id} value={son.titre}>
                      {son.titre}
                    </option>
                  ))}
                </Form.Select>
                {annonceAccueil !== '' && (
                  <i
                    className="bi bi-play-fill text-primary fs-3 cursor-pointer"
                    onClick={jouerApercu}
                  />
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card bg="light" className="mb-5">
        <Card.Body>
          <div className="fw-bold text-uppercase fs-6 mb-4">
            {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.ACTIONS_TITLE'})}
          </div>

          {actions.length > 0 && (
            <table className="table table-sm mb-4">
              <thead>
                <tr className="table-primary">
                  <th>{intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.TOUCHE'})}</th>
                  <th>{intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.ACTION'})}</th>
                  <th>{intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.DESTINATAIRE'})}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {actions.map((action) => (
                  <tr key={action.touche}>
                    <td>
                      <Badge text={action.touche} color="dark" light size="small" />
                    </td>
                    <td>{intl.formatMessage({id: ACTION_LABEL_ID[action.type]})}</td>
                    <td>
                      <DestinataireTexte action={action} />
                    </td>
                    <td className="text-end">
                      <ActionIcon
                        label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.EDIT'})}
                        faIcon="fs-4 bi bi-pencil"
                        onClick={() => commencerEditionAction(action)}
                      />
                      <ActionIcon
                        label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                        faIcon="fs-4 bi bi-trash text-danger"
                        onClick={() => supprimerAction(action.touche)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Row className="g-3 align-items-end">
            <Col xs={12} md={2}>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.TOUCHE'})}
              </Form.Label>
              <Form.Select value={toucheChoisie} onChange={(e) => setToucheChoisie(e.target.value)}>
                {touchesDisponibles.map((touche) => (
                  <option key={touche} value={touche}>
                    {touche}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.ACTION'})}
              </Form.Label>
              <Form.Select
                value={typeChoisi}
                onChange={(e) => {
                  setTypeChoisi(e.target.value as TypeActionIvr);
                  setCibleChoisie('');
                  setSaisieLibreGroupe(false);
                  setSaisieLibreNumero(false);
                }}
              >
                {TYPES_ACTION.map((type) => (
                  <option key={type} value={type}>
                    {intl.formatMessage({id: ACTION_LABEL_ID[type]})}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={5}>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.DESTINATAIRE'})}
              </Form.Label>
              {typeChoisi === 'groupe' && (
                <div className="d-flex align-items-center gap-2">
                  {saisieLibreGroupe ? (
                    <Form.Control
                      value={cibleChoisie}
                      placeholder="200"
                      onChange={(e) => setCibleChoisie(e.target.value)}
                    />
                  ) : (
                    <Form.Select value={cibleChoisie} onChange={(e) => setCibleChoisie(e.target.value)}>
                      <option value="">
                        {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.CHOISIR_GROUPE'})}
                      </option>
                      {groupesData.groupes.map((groupe) => (
                        <option key={groupe.id} value={groupe.nom}>
                          {groupe.nom}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                  <i
                    className={`bi ${saisieLibreGroupe ? 'bi-search' : 'bi-pencil'} text-primary cursor-pointer`}
                    title={intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.SAISIE_LIBRE'})}
                    onClick={() => {
                      setSaisieLibreGroupe(!saisieLibreGroupe);
                      setCibleChoisie('');
                    }}
                  />
                </div>
              )}
              {typeChoisi === 'numero' && (
                <div className="d-flex align-items-center gap-2">
                  {saisieLibreNumero ? (
                    <Form.Control
                      value={cibleChoisie}
                      placeholder="+41791234567"
                      onChange={(e) => setCibleChoisie(e.target.value)}
                    />
                  ) : (
                    <Form.Select value={cibleChoisie} onChange={(e) => setCibleChoisie(e.target.value)}>
                      <option value="">
                        {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.CHOISIR_NUMERO'})}
                      </option>
                      {activiteData.lignes.map((ligne) => (
                        <option key={ligne.telephone} value={ligne.telephone}>
                          {ligne.telephone}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                  <i
                    className={`bi ${saisieLibreNumero ? 'bi-search' : 'bi-pencil'} text-primary cursor-pointer`}
                    title={intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.SAISIE_LIBRE'})}
                    onClick={() => {
                      setSaisieLibreNumero(!saisieLibreNumero);
                      setCibleChoisie('');
                    }}
                  />
                </div>
              )}
              {typeChoisi === 'annonce' && (
                <Form.Select value={cibleChoisie} onChange={(e) => setCibleChoisie(e.target.value)}>
                  <option value="">
                    {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.CHOISIR_ANNONCE'})}
                  </option>
                  {annoncesData.map((son) => (
                    <option key={son.id} value={son.titre}>
                      {son.titre}
                    </option>
                  ))}
                </Form.Select>
              )}
              {(typeChoisi === 'fin_appel' || typeChoisi === 'rejouer_menu') && (
                <Form.Control disabled value="—" />
              )}
            </Col>
            <Col xs={12} md={2}>
              <Button
                label={intl.formatMessage({
                  id: toucheEnEdition
                    ? 'TELEPHONIE.SVI.MENU.FORM.SAVE'
                    : 'TELEPHONIE.SVI.IVR.FORM.AJOUTER_ACTION',
                })}
                disabled={!actionValide}
                onClick={validerAction}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-center gap-3">
        <BootstrapButton variant="secondary" onClick={onAnnuler}>
          {intl.formatMessage({id: 'CANCEL'})}
        </BootstrapButton>
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
          disabled={nom.trim() === ''}
          onClick={enregistrer}
        />
      </div>
    </div>
  );
};

const ConfigurationSvi = (): JSX.Element | null => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {data, isLoading} = useGetIvrs();
  const {data: numerosData} = useGetVirtualPbxNumeros();
  const [ivrsLocal, setIvrsLocal] = useState<Ivr[] | null>(null);
  const [vue, setVue] = useState<'liste' | 'formulaire'>('liste');
  const [ivrEnEdition, setIvrEnEdition] = useState<Ivr | null>(null);
  const [ivrASupprimer, setIvrASupprimer] = useState<Ivr | null>(null);
  const [erreurSuppression, setErreurSuppression] = useState<{nom: string; numero: string} | null>(
    null
  );
  const [message, setMessage] = useAutoDismissMessage();

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  const ivrs = ivrsLocal ?? data;

  const mettreAJour = (ivrsMisAJour: Ivr[]): void => {
    setIvrsLocal(ivrsMisAJour);
    queryClient.setQueryData(IVRS_QUERY_KEY, ivrsMisAJour);
  };

  const ouvrirAjout = (): void => {
    setIvrEnEdition(null);
    setVue('formulaire');
  };

  const ouvrirEdition = (ivr: Ivr): void => {
    setIvrEnEdition(ivr);
    setVue('formulaire');
  };

  const enregistrer = (ivrSaisi: Omit<Ivr, 'id'>): void => {
    const ivrsMisAJour = ivrEnEdition
      ? ivrs.map((ivr) => (ivr.id === ivrEnEdition.id ? {id: ivr.id, ...ivrSaisi} : ivr))
      : [...ivrs, {id: Math.max(0, ...ivrs.map((ivr) => ivr.id)) + 1, ...ivrSaisi}];
    mettreAJour(ivrsMisAJour);
    setVue('liste');
    setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
  };

  const demanderSuppression = (ivr: Ivr): void => {
    const numero = (numerosData?.numerosGlobaux ?? []).find((n) =>
      n.plages.some((plage) => plage.nomGroupe === ivr.nom)
    );
    if (numero) {
      setErreurSuppression({nom: ivr.nom, numero: numero.numeroFormate});
      return;
    }
    setErreurSuppression(null);
    setIvrASupprimer(ivr);
  };

  const confirmerSuppression = (): void => {
    if (ivrASupprimer) {
      mettreAJour(ivrs.filter((ivr) => ivr.id !== ivrASupprimer.id));
      setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
    }
    setIvrASupprimer(null);
  };

  if (vue === 'formulaire') {
    return (
      <FormulaireIvr
        ivrInitial={ivrEnEdition ?? undefined}
        onAnnuler={() => setVue('liste')}
        onEnregistrer={enregistrer}
      />
    );
  }

  const boutonAjouterVisible = ivrs.length < IVR_MAX;
  const boutonAjouterBasVisible = ivrs.length >= 2 && ivrs.length < IVR_MAX;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div className="d-flex align-items-center gap-2">
          <Level3Title
            title={intl.formatMessage(
              {id: 'TELEPHONIE.SVI.IVR.LIST_TITLE'},
              {count: ivrs.length, max: IVR_MAX}
            )}
          />
          <OverlayTrigger
            overlay={<Tooltip>{intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.MAX_TOOLTIP'}, {max: IVR_MAX})}</Tooltip>}
          >
            <i className="bi bi-question-circle-fill text-primary cursor-pointer" />
          </OverlayTrigger>
        </div>
        {boutonAjouterVisible && (
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.TITLE_ADD'})}
            faIcon="bi bi-plus-lg fs-6 me-2"
            onClick={ouvrirAjout}
          />
        )}
      </div>
      <p className="text-muted fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.DESCRIPTION'})}
      </p>

      {message && <div className="fw-bold text-danger mb-3">{message}</div>}
      {erreurSuppression && (
        <div className="alert alert-danger">
          {intl.formatMessage(
            {id: 'TELEPHONIE.SVI.IVR.ERREUR_SUPPRESSION'},
            {
              nom: erreurSuppression.nom,
              numero: erreurSuppression.numero,
              b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
            }
          )}
        </div>
      )}

      <Row className="g-5">
        {ivrs.map((ivr) => (
          <Col key={ivr.id} xs={12} xl={6}>
            <Card className="h-100">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <Card.Title className="m-0">
                    <Level3Title
                      title={intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.CARD_TITLE'}, {nom: ivr.nom})}
                    />
                  </Card.Title>
                  <Badge text={ivr.numeroCourt} color="primary" light size="small" />
                </div>
                <div>
                  <ActionIcon
                    label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.EDIT'})}
                    faIcon="fs-3 bi bi-pencil"
                    onClick={() => ouvrirEdition(ivr)}
                  />
                  <ActionIcon
                    label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                    faIcon="fs-3 bi bi-trash text-danger"
                    onClick={() => demanderSuppression(ivr)}
                  />
                </div>
              </Card.Header>
              <Card.Body>
                <div className="d-flex align-items-center gap-3 mb-4">
                  <span className="fw-semibold fs-6">
                    {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.MENU_VOCAL_LABEL'})}
                  </span>
                  <span className="fs-6">
                    {ivr.annonceAccueil ?? intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.AUCUN'})}
                  </span>
                  {ivr.annonceAccueil && (
                    <i
                      className="bi bi-play-fill text-primary fs-4 cursor-pointer"
                      onClick={jouerApercu}
                    />
                  )}
                </div>

                {ivr.actions.length === 0 ? (
                  <div className="text-muted fs-6 fst-italic">
                    {intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.AUCUNE_ACTION'})}
                  </div>
                ) : (
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr className="table-primary">
                        <th>{intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.TOUCHE'})}</th>
                        <th>{intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.ACTION'})}</th>
                        <th>{intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.TABLE.DESTINATAIRE'})}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ivr.actions.map((action) => (
                        <tr key={action.touche}>
                          <td>
                            <Badge text={action.touche} color="dark" light size="small" />
                          </td>
                          <td>{intl.formatMessage({id: ACTION_LABEL_ID[action.type]})}</td>
                          <td>
                            <DestinataireTexte action={action} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {boutonAjouterBasVisible && (
        <div className="d-flex justify-content-center mt-5">
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.SVI.IVR.FORM.TITLE_ADD'})}
            faIcon="bi bi-plus-lg fs-6 me-2"
            onClick={ouvrirAjout}
          />
        </div>
      )}

      <Modal show={ivrASupprimer != null} onHide={() => setIvrASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage(
            {id: 'TELEPHONIE.SVI.IVR.DELETE_BODY'},
            {
              nom: ivrASupprimer?.nom ?? '',
              b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
            }
          )}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setIvrASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppression}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ConfigurationSvi;
